import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_COURSE_SKILLS,
  NEED_SOURCES,
  NEED_SOURCE_MAP,
  NEED_STATUSES,
  NEED_STATUS_MAP,
  SESSION_STATUSES,
  SESSION_STATUS_MAP,
  TRACK_DEFS,
  TRACK_MAP,
  TRAINING_KINDS,
  TRAINING_KIND_MAP,
  type NeedSource,
  type TrainingKindKey,
  type TrainingTrack,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api, qs } from '../../lib/api';
import { date, money, num, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';

/* ==========================================================================
   الصفوف كما يرسلها الخادم
   ========================================================================== */

export interface NeedRow {
  id: string;
  title: string;
  description: string | null;
  source: NeedSource;
  sourceLabel: string;
  sourceKind: string;
  measured: boolean;
  status: 'OPEN' | 'PLANNED' | 'MET' | 'DROPPED';
  statusLabel: string;
  statusKind: string;
  statusNote: string;
  live: boolean;
  priority: number;
  priorityLabel: string;
  dueOn: string | null;
  notes: string | null;
  outcome: string | null;
  overdue: boolean;
  employee: { id: string; name: string; code: string | null } | null;
  position: { id: string; title: string } | null;
}

export interface CourseRow {
  id: string;
  code: string | null;
  title: string;
  kind: TrainingKindKey;
  kindLabel: string;
  kindKind: string;
  provider: string | null;
  description: string | null;
  hours: number | null;
  defaultCost: number | null;
  currency: string;
  track: TrainingTrack;
  trackLabel: string;
  trackKind: string;
  skills: string[];
  isActive: boolean;
  sessions: number;
}

export interface SessionRow {
  id: string;
  course: { id: string; title: string; kind: string; track: string };
  startsOn: string | null;
  endsOn: string | null;
  location: string | null;
  trainer: string | null;
  capacity: number | null;
  cost: number | null;
  currency: string;
  status: 'PLANNED' | 'RUNNING' | 'DONE' | 'CANCELLED';
  statusLabel: string;
  statusKind: string;
  statusNote: string;
  enrolling: boolean;
  results: boolean;
  notes: string | null;
  seats: number;
  attended: number;
  pending: number;
  left: number | null;
  costPerSeat: number | null;
}

interface ImpactView {
  minFeedback: number;
  minImpact: number;
  rows: {
    courseId: string;
    title: string;
    trackLabel: string;
    sessions: number;
    seats: number;
    attended: number;
    absent: number;
    attendance: number | null;
    cost: number | null;
    costPerSeat: number | null;
    rating: number | null;
    ratingReason: string | null;
    score: number | null;
    delta: number | null;
    deltaCount: number;
    deltaReason: string | null;
  }[];
}

interface Summary {
  openNeeds: number;
  urgent: number;
  courses: number;
  upcoming: number;
  seats: number;
}

interface EmployeeLite {
  id: string;
  name: string;
  code: string | null;
}

interface PositionLite {
  id: string;
  title: string;
}

type Tab = 'needs' | 'courses' | 'sessions' | 'impact';

const TABS: readonly { key: Tab; label: string; adminOnly?: boolean }[] = [
  { key: 'needs', label: 'الاحتياجات' },
  { key: 'courses', label: 'كتالوج الدورات' },
  { key: 'sessions', label: 'الانعقادات' },
  { key: 'impact', label: 'الأثر والتكلفة', adminOnly: true },
] as const;

/* ==========================================================================
   الصفحة
   ========================================================================== */

/**
 * التدريب والتطوير.
 *
 * ## احتياجٌ، ثمّ دورة، ثمّ انعقاد، ثمّ مقعد
 *
 * الاحتياجُ يُكتب لشخصٍ أو لمقعدٍ في الهيكل، ويحمل من أين جاء: من تقييمٍ
 * قِيس أم من رغبةٍ طُلبت — والفرقُ بينهما هو ما يقرّر أين تُنفَق الميزانيّة.
 * والدورةُ في الكتالوج غيرُ انعقادها: واحدةٌ تُعقد ثلاثَ مرّاتٍ بثلاثة
 * مدرّبين وثلاث تكاليف.
 *
 * ## وما لا يقع هنا
 *
 * لا محتوى ولا اختباراتٍ ولا فيديو: هذا سجلُّ ما وقع لا منصّةُ تعلُّم.
 * والمحتوى عند مقدّمه، وما يُكتب هنا أنّ فلانًا حضر في يومٍ بعينه ونالَ
 * شهادةً برقمها.
 */
export function Learning() {
  const { atLeast } = useAuth();
  const canAdmin = atLeast('ADMIN');
  const [tab, setTab] = useState<Tab>('needs');

  const summary = useQuery({
    queryKey: ['learning', 'summary'],
    queryFn: () => api<Summary>('/hr/learning/summary'),
  });

  const s = summary.data;
  const tabs = TABS.filter((t) => !t.adminOnly || canAdmin);

  return (
    <div className="page">
      <PageHead
        title="التدريب والتطوير"
        subtitle="ما ينقص الفريقَ يُكتب، ثمّ يُدرَج في دورةٍ تنعقد، ثمّ يُقاس ما أنتجه بما يصحّ قياسُه"
      />

      {summary.isError && (
        <ErrorState
          error={summary.error}
          onRetry={() => void summary.refetch()}
        />
      )}

      {s && (
        <div className="stats">
          <Stat
            label="احتياجاتٌ مكتوبة"
            value={num(s.openNeeds)}
            note={
              s.urgent > 0 ? `منها ${num(s.urgent)} عاجل` : 'لا عاجلَ بينها'
            }
          />
          <Stat
            label="دوراتٌ في الكتالوج"
            value={num(s.courses)}
            note="المفعّلةُ وحدَها"
          />
          <Stat
            label="انعقاداتٌ قادمة"
            value={num(s.upcoming)}
            note="مجدولةٌ أو جارية"
          />
          <Stat
            label="مقاعدُ شُغلت"
            value={num(s.seats)}
            note="في كلّ الانعقادات"
          />
        </div>
      )}

      {s && s.urgent > 0 && (
        <Notice title="احتياجاتٌ عاجلة" icon="alert" kind="warning">
          {plural(s.urgent, WORDS.record)} بأولويّة «عاجل» لم يُدرَج في دورةٍ
          بعد. والعاجلُ الذي يبقى مكتوبًا سنةً لم يكن عاجلًا.
        </Notice>
      )}

      <div className="tabs" role="tablist" aria-label="أقسام التدريب">
        {tabs.map((t) => (
          <button
            key={t.key}
            className="tab"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'needs' && <NeedsSection />}
      {tab === 'courses' && <CoursesSection canAdmin={canAdmin} />}
      {tab === 'sessions' && <SessionsSection canAdmin={canAdmin} />}
      {tab === 'impact' && canAdmin && <ImpactSection />}
    </div>
  );
}

/* ==========================================================================
   الاحتياجات
   ========================================================================== */

function NeedsSection() {
  const [status, setStatus] = useState('OPEN');
  const [source, setSource] = useState('');
  const [writing, setWriting] = useState(false);
  const [editing, setEditing] = useState<NeedRow | null>(null);
  const [dropping, setDropping] = useState<NeedRow | null>(null);

  const query = { status: status || undefined, source: source || undefined };

  const list = useQuery({
    queryKey: ['learning', 'needs', query],
    queryFn: () => api<NeedRow[]>(`/hr/learning/needs${qs(query)}`),
  });

  const rows = list.data ?? [];

  return (
    <div className="card mt-3">
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <h2 className="card-title" style={{ flex: 1 }}>
          الاحتياجات التدريبية
        </h2>
        <select
          className="select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">كل الحالات</option>
          {NEED_STATUSES.map((k) => (
            <option key={k} value={k}>
              {NEED_STATUS_MAP[k].label}
            </option>
          ))}
        </select>
        <select
          className="select"
          value={source}
          onChange={(e) => setSource(e.target.value)}
        >
          <option value="">كل المصادر</option>
          {NEED_SOURCES.map((k) => (
            <option key={k} value={k}>
              {NEED_SOURCE_MAP[k].label}
            </option>
          ))}
        </select>
        <button className="btn btn-primary" onClick={() => setWriting(true)}>
          <Icon name="plus" size={15} />
          احتياج
        </button>
      </div>

      {/*
        المصدرُ يُعرض في عمودٍ لأنّه يغيّر القراءة: فجوةٌ قِيست في تقييمٍ
        غيرُ رغبةٍ طلبها صاحبُها، وخلطُهما ينفق الميزانيّةَ على ما يُطلَب
        لا على ما ينقص.
      */}
      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا احتياجات"
          text="الاحتياجُ يُكتب لموظّفٍ بعينه أو لمقعدٍ في الهيكل: «كلُّ من يشغل خدمة العملاء يحتاج دورةَ التعامل مع الغاضبين» احتياجُ وظيفةٍ يبقى بعد أن يتركها من يشغلها اليوم."
          action={
            <button
              className="btn btn-primary"
              onClick={() => setWriting(true)}
            >
              اكتب أوّل احتياج
            </button>
          }
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap sticky-head mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الاحتياج</th>
                <th>لمن</th>
                <th>من أين</th>
                <th>الأولويّة</th>
                <th>الموعد</th>
                <th>الحالة</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((n) => (
                <tr key={n.id}>
                  <td>
                    {n.title}
                    {n.description && (
                      <div className="tiny mute">{n.description}</div>
                    )}
                  </td>
                  <td className="tiny">
                    {n.employee?.name ??
                      (n.position ? `كلّ من يشغل: ${n.position.title}` : '—')}
                  </td>
                  <td>
                    <Badge kind={n.sourceKind}>{n.sourceLabel}</Badge>
                    {!n.measured && (
                      <div className="tiny mute">رأيٌ لا قياس</div>
                    )}
                  </td>
                  <td className="tiny">{n.priorityLabel}</td>
                  <td className="tiny">
                    {n.dueOn ? date(n.dueOn) : '—'}
                    {n.overdue && (
                      <div className="tiny" style={{ color: 'var(--warning)' }}>
                        مضى
                      </div>
                    )}
                  </td>
                  <td>
                    <Badge kind={n.statusKind}>{n.statusLabel}</Badge>
                  </td>
                  <td>
                    {n.live && (
                      <div className="row" style={{ gap: 4 }}>
                        <button
                          className="btn btn-sm"
                          onClick={() => setEditing(n)}
                        >
                          تعديل
                        </button>
                        <button
                          className="btn btn-sm"
                          onClick={() => setDropping(n)}
                        >
                          أسقِطه
                        </button>
                      </div>
                    )}
                    {!n.live && n.outcome && (
                      <span className="tiny mute">{n.outcome}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(writing || editing) && (
        <NeedDialog
          need={editing}
          onClose={() => {
            setWriting(false);
            setEditing(null);
          }}
        />
      )}
      {dropping && (
        <DropNeedDialog need={dropping} onClose={() => setDropping(null)} />
      )}
    </div>
  );
}

function NeedDialog({
  need,
  onClose,
}: {
  need: NeedRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [forWhat, setForWhat] = useState<'employee' | 'position'>(
    need?.position && !need.employee ? 'position' : 'employee',
  );
  const [employeeId, setEmployeeId] = useState(need?.employee?.id ?? '');
  const [positionId, setPositionId] = useState(need?.position?.id ?? '');
  const [title, setTitle] = useState(need?.title ?? '');
  const [description, setDescription] = useState(need?.description ?? '');
  const [source, setSource] = useState<NeedSource>(need?.source ?? 'MANAGER');
  const [priority, setPriority] = useState(String(need?.priority ?? 2));
  const [dueOn, setDueOn] = useState(need?.dueOn ?? '');
  const [notes, setNotes] = useState(need?.notes ?? '');

  const staff = useQuery({
    queryKey: ['employees', { status: 'ACTIVE', pageSize: 200 }],
    queryFn: () =>
      api<{ items: EmployeeLite[] }>(
        '/hr/employees?status=ACTIVE&pageSize=200',
      ),
    enabled: forWhat === 'employee',
  });

  const positions = useQuery({
    queryKey: ['org', 'positions'],
    queryFn: () => api<PositionLite[]>('/hr/org/positions'),
    enabled: forWhat === 'position',
  });

  const save = useMutation({
    mutationFn: () =>
      api(need ? `/hr/learning/needs/${need.id}` : '/hr/learning/needs', {
        method: need ? 'PATCH' : 'POST',
        body: {
          employeeId: forWhat === 'employee' ? employeeId || null : null,
          positionId: forWhat === 'position' ? positionId || null : null,
          title,
          description: description || null,
          source,
          priority: Number(priority),
          dueOn: dueOn || null,
          notes: notes || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['learning'] });
      toast('حُفظ الاحتياج.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const ready =
    title.trim().length >= 3 &&
    (forWhat === 'employee' ? Boolean(employeeId) : Boolean(positionId));

  return (
    <Modal
      title={need ? 'تعديل احتياج' : 'احتياج تدريبيّ جديد'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending || !ready}
            onClick={() => save.mutate()}
          >
            احفظ
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <span className="label">لمن يُكتب</span>
          {/*
            الاحتياجُ لمقعدٍ يبقى بعد أن يتركه من يشغله اليوم: «كلُّ من يشغل
            خدمة العملاء يحتاج كذا» لا تُعاد كتابتُها مع كلّ تعيين.
          */}
          <div className="row" style={{ gap: 'var(--sp-2)' }}>
            <label className="row tiny" style={{ gap: 6 }}>
              <input
                type="radio"
                checked={forWhat === 'employee'}
                onChange={() => setForWhat('employee')}
              />
              لموظّفٍ بعينه
            </label>
            <label className="row tiny" style={{ gap: 6 }}>
              <input
                type="radio"
                checked={forWhat === 'position'}
                onChange={() => setForWhat('position')}
              />
              لكلّ من يشغل وصفًا وظيفيًّا
            </label>
          </div>
        </div>

        {forWhat === 'employee' ? (
          <div className="field span-2">
            <label className="label req" htmlFor="tn-emp">
              الموظّف
            </label>
            <select
              id="tn-emp"
              className="select"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
            >
              <option value="">— اختر —</option>
              {(staff.data?.items ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="field span-2">
            <label className="label req" htmlFor="tn-pos">
              الوصف الوظيفي
            </label>
            <select
              id="tn-pos"
              className="select"
              value={positionId}
              onChange={(e) => setPositionId(e.target.value)}
            >
              <option value="">— اختر —</option>
              {(positions.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="field span-2">
          <label className="label req" htmlFor="tn-title">
            ما ينقصه
          </label>
          <input
            id="tn-title"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="التعامل مع العميل الغاضب"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="tn-source">
            من أين عُرف
          </label>
          <select
            id="tn-source"
            className="select"
            value={source}
            onChange={(e) => setSource(e.target.value as NeedSource)}
          >
            {NEED_SOURCES.map((k) => (
              <option key={k} value={k}>
                {NEED_SOURCE_MAP[k].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            {NEED_SOURCE_MAP[source].description}
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="tn-priority">
            الأولويّة
          </label>
          <select
            id="tn-priority"
            className="select"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <option value="1">عاجل</option>
            <option value="2">مهمّ</option>
            <option value="3">متى أمكن</option>
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="tn-due">
            الموعد
          </label>
          <input
            id="tn-due"
            className="input"
            type="date"
            value={dueOn}
            onChange={(e) => setDueOn(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="tn-desc">
            تفصيل
          </label>
          <textarea
            id="tn-desc"
            className="input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="ما الذي وقع فدلّ على أنّ هذا ينقصه"
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="tn-notes">
            ملاحظات
          </label>
          <textarea
            id="tn-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

function DropNeedDialog({
  need,
  onClose,
}: {
  need: NeedRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [outcome, setOutcome] = useState('');

  const drop = useMutation({
    mutationFn: () =>
      api(`/hr/learning/needs/${need.id}/close`, {
        method: 'POST',
        body: { status: 'DROPPED', outcome },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['learning'] });
      toast('أُسقط الاحتياج.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title="إسقاط احتياج"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-danger"
            disabled={drop.isPending || outcome.trim().length < 3}
            onClick={() => drop.mutate()}
          >
            أسقِطه
          </button>
        </>
      }
    >
      <Notice title="و«سُدّ» لا تُكتب من هنا" icon="info">
        الاحتياجُ يُسدّ بتسجيل حضور صاحبه في دورةٍ — فيشهد النظامُ كيف سُدّ.
        وهذا البابُ لمن لم يعد الاحتياجُ مطلوبًا منه.
      </Notice>

      <div className="field mt-3">
        <label className="label req" htmlFor="dn-outcome">
          لماذا لم يعد مطلوبًا
        </label>
        <textarea
          id="dn-outcome"
          className="input"
          rows={3}
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          placeholder={`«${need.title}» — ما الذي تغيّر`}
        />
        <span className="tiny mute">
          بلا سببٍ مكتوبٍ يكتبه غيرُك من جديدٍ بعد سنة.
        </span>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   الكتالوج
   ========================================================================== */

function CoursesSection({ canAdmin }: { canAdmin: boolean }) {
  const [track, setTrack] = useState('');
  const [writing, setWriting] = useState(false);
  const [editing, setEditing] = useState<CourseRow | null>(null);

  const query = { track: track || undefined, all: '1' as const };

  const list = useQuery({
    queryKey: ['learning', 'courses', query],
    queryFn: () => api<CourseRow[]>(`/hr/learning/courses${qs(query)}`),
  });

  const rows = list.data ?? [];

  return (
    <div className="card mt-3">
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <h2 className="card-title" style={{ flex: 1 }}>
          كتالوج الدورات
        </h2>
        <select
          className="select"
          value={track}
          onChange={(e) => setTrack(e.target.value)}
        >
          <option value="">كل المسارات</option>
          {TRACK_DEFS.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>
        {canAdmin && (
          <button className="btn btn-primary" onClick={() => setWriting(true)}>
            <Icon name="plus" size={15} />
            دورة
          </button>
        )}
      </div>

      {track === 'LEADERSHIP' && (
        <Notice title="برامج القيادة" icon="star">
          تُقرأ مع خطط الإحلال: المرشَّحُ الذي كُتب عنه «جاهزٌ خلال سنة» هذا ما
          يحتاجه فيها.{' '}
          <Link className="link" to="/hr/org/succession">
            خطط الإحلال
          </Link>
          .
        </Notice>
      )}

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا دورات"
          text="الدورةُ هنا في الكتالوج، وانعقادُها في بابه: واحدةٌ تُعقد ثلاثَ مرّاتٍ بثلاثة مدرّبين وثلاث تكاليف."
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap sticky-head mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الدورة</th>
                <th>المسار</th>
                <th>الشكل</th>
                <th>الجهة</th>
                <th>الساعات</th>
                <th>تكلفةٌ مرجعيّة</th>
                <th>انعقادات</th>
                {canAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr
                  key={c.id}
                  style={c.isActive ? undefined : { opacity: 0.55 }}
                >
                  <td>
                    {c.title}
                    {c.skills.length > 0 && (
                      <div className="tiny mute">{c.skills.join(' · ')}</div>
                    )}
                    {!c.isActive && (
                      <div className="tiny mute">موقوفة — لا تُعقد جديدًا</div>
                    )}
                  </td>
                  <td>
                    <Badge kind={c.trackKind}>{c.trackLabel}</Badge>
                  </td>
                  <td className="tiny">{c.kindLabel}</td>
                  <td className="tiny">{c.provider ?? '—'}</td>
                  <td className="num">
                    {c.hours === null ? '—' : num(c.hours)}
                  </td>
                  <td className="num">
                    {c.defaultCost === null
                      ? '—'
                      : money(c.defaultCost, c.currency)}
                  </td>
                  <td className="num">{num(c.sessions)}</td>
                  {canAdmin && (
                    <td>
                      <button
                        className="btn btn-sm"
                        onClick={() => setEditing(c)}
                      >
                        تعديل
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(writing || editing) && (
        <CourseDialog
          course={editing}
          onClose={() => {
            setWriting(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function CourseDialog({
  course,
  onClose,
}: {
  course: CourseRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [code, setCode] = useState(course?.code ?? '');
  const [title, setTitle] = useState(course?.title ?? '');
  const [kind, setKind] = useState<TrainingKindKey>(course?.kind ?? 'INTERNAL');
  const [provider, setProvider] = useState(course?.provider ?? '');
  const [description, setDescription] = useState(course?.description ?? '');
  const [hours, setHours] = useState(
    course?.hours === null || course?.hours === undefined
      ? ''
      : String(course.hours),
  );
  const [defaultCost, setDefaultCost] = useState(
    course?.defaultCost === null || course?.defaultCost === undefined
      ? ''
      : String(course.defaultCost),
  );
  const [track, setTrack] = useState<TrainingTrack>(course?.track ?? 'GENERAL');
  const [skills, setSkills] = useState(course?.skills.join('، ') ?? '');
  const [isActive, setIsActive] = useState(course?.isActive ?? true);

  const save = useMutation({
    mutationFn: () =>
      api(
        course ? `/hr/learning/courses/${course.id}` : '/hr/learning/courses',
        {
          method: course ? 'PATCH' : 'POST',
          body: {
            code: code || null,
            title,
            kind,
            provider: provider || null,
            description: description || null,
            hours: hours === '' ? null : Number(hours),
            defaultCost: defaultCost === '' ? null : Number(defaultCost),
            track,
            skills: skills
              .split(/[،,]/)
              .map((x) => x.trim())
              .filter((x) => x.length >= 2)
              .slice(0, MAX_COURSE_SKILLS),
            isActive,
          },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['learning'] });
      toast('حُفظت الدورة.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={course ? 'تعديل دورة' : 'دورة جديدة في الكتالوج'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending || title.trim().length < 3}
            onClick={() => save.mutate()}
          >
            احفظ
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="tc-title">
            اسم الدورة
          </label>
          <input
            id="tc-title"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="tc-code">
            الكود
          </label>
          <input
            id="tc-code"
            className="input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="tc-track">
            المسار
          </label>
          <select
            id="tc-track"
            className="select"
            value={track}
            onChange={(e) => setTrack(e.target.value as TrainingTrack)}
          >
            {TRACK_DEFS.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
          <span className="tiny mute">{TRACK_MAP[track].description}</span>
        </div>

        <div className="field">
          <label className="label" htmlFor="tc-kind">
            الشكل
          </label>
          <select
            id="tc-kind"
            className="select"
            value={kind}
            onChange={(e) => setKind(e.target.value as TrainingKindKey)}
          >
            {TRAINING_KINDS.map((k) => (
              <option key={k} value={k}>
                {TRAINING_KIND_MAP[k].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            {TRAINING_KIND_MAP[kind].description}
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="tc-provider">
            الجهة المقدِّمة
          </label>
          <input
            id="tc-provider"
            className="input"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            disabled={!TRAINING_KIND_MAP[kind].hasProvider}
            placeholder={
              TRAINING_KIND_MAP[kind].hasProvider ? '' : 'داخليّة — بلا جهة'
            }
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="tc-hours">
            الساعات
          </label>
          <input
            id="tc-hours"
            className="input num"
            type="number"
            min={0}
            step={0.5}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="tc-cost">
            تكلفةٌ مرجعيّة للمشارك
          </label>
          <input
            id="tc-cost"
            className="input num"
            type="number"
            min={0}
            value={defaultCost}
            onChange={(e) => setDefaultCost(e.target.value)}
          />
          {/*
            المرجعيّةُ للتخطيط، والمحسوبةُ من تكلفة الانعقاد نفسِه: انعقادٌ
            بمدرّبٍ أغلى ليس دورةً أخرى.
          */}
          <span className="tiny mute">
            للتخطيط وحده — والتكلفةُ التي تُحسب هي تكلفةُ الانعقاد نفسِه.
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="tc-skills">
            ما تبنيه من مهارات
          </label>
          <input
            id="tc-skills"
            className="input"
            value={skills}
            onChange={(e) => setSkills(e.target.value)}
            placeholder="الإنصات، احتواء الغضب، تصعيد الشكوى"
          />
          <span className="tiny mute">
            تُفصل بفاصلة — نصوصٌ تُقرأ بالعين لا مفاتيحُ تُطابَق.
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="tc-desc">
            الوصف
          </label>
          <textarea
            id="tc-desc"
            className="input"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            مفعّلة — تُعقد جديدًا
          </label>
          <span className="tiny mute">
            الموقوفةُ تبقى بانعقاداتها وتاريخها، ولا تُعقد مرّةً أخرى.
          </span>
        </div>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   الانعقادات
   ========================================================================== */

function SessionsSection({ canAdmin }: { canAdmin: boolean }) {
  const [status, setStatus] = useState('');
  const [writing, setWriting] = useState(false);

  const query = { status: status || undefined };

  const list = useQuery({
    queryKey: ['learning', 'sessions', query],
    queryFn: () => api<SessionRow[]>(`/hr/learning/sessions${qs(query)}`),
  });

  const rows = list.data ?? [];

  return (
    <div className="card mt-3">
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <h2 className="card-title" style={{ flex: 1 }}>
          الانعقادات
        </h2>
        <select
          className="select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">كل الحالات</option>
          {SESSION_STATUSES.map((k) => (
            <option key={k} value={k}>
              {SESSION_STATUS_MAP[k].label}
            </option>
          ))}
        </select>
        {canAdmin && (
          <button className="btn btn-primary" onClick={() => setWriting(true)}>
            <Icon name="plus" size={15} />
            انعقاد
          </button>
        )}
      </div>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا انعقادات"
          text="الانعقادُ تاريخٌ ومدرّبٌ وتكلفةٌ ومقاعد — ومنه يُقاس الحضورُ والكلفة."
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap sticky-head mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الدورة</th>
                <th>التاريخ</th>
                <th>المدرّب</th>
                <th>المقاعد</th>
                <th>حضر</th>
                <th>كلفة المقعد</th>
                <th>الحالة</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td>{s.course.title}</td>
                  <td className="tiny">
                    {date(s.startsOn ?? '')} — {date(s.endsOn ?? '')}
                  </td>
                  <td className="tiny">{s.trainer ?? '—'}</td>
                  <td className="num">
                    {num(s.seats)}
                    {s.capacity !== null ? ` / ${num(s.capacity)}` : ''}
                  </td>
                  <td className="num">
                    {num(s.attended)}
                    {s.pending > 0 && (
                      <div className="tiny" style={{ color: 'var(--warning)' }}>
                        {num(s.pending)} لم يُحسم
                      </div>
                    )}
                  </td>
                  <td className="num">
                    {s.costPerSeat === null
                      ? '—'
                      : money(s.costPerSeat, s.currency)}
                  </td>
                  <td>
                    <Badge kind={s.statusKind}>{s.statusLabel}</Badge>
                  </td>
                  <td>
                    <Link
                      className="btn btn-sm"
                      to={`/hr/learning/sessions/${s.id}`}
                    >
                      افتحه
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {writing && <SessionDialog onClose={() => setWriting(false)} />}
    </div>
  );
}

export function SessionDialog({
  session,
  onClose,
}: {
  session?: SessionRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const today = new Date().toISOString().slice(0, 10);
  const [courseId, setCourseId] = useState(session?.course.id ?? '');
  const [startsOn, setStartsOn] = useState(session?.startsOn ?? today);
  const [endsOn, setEndsOn] = useState(session?.endsOn ?? today);
  const [location, setLocation] = useState(session?.location ?? '');
  const [trainer, setTrainer] = useState(session?.trainer ?? '');
  const [capacity, setCapacity] = useState(
    session?.capacity === null || session?.capacity === undefined
      ? ''
      : String(session.capacity),
  );
  const [cost, setCost] = useState(
    session?.cost === null || session?.cost === undefined
      ? ''
      : String(session.cost),
  );
  const [notes, setNotes] = useState(session?.notes ?? '');

  const courses = useQuery({
    queryKey: ['learning', 'courses', {}],
    queryFn: () => api<CourseRow[]>('/hr/learning/courses'),
  });

  const save = useMutation({
    mutationFn: () =>
      api(
        session
          ? `/hr/learning/sessions/${session.id}`
          : '/hr/learning/sessions',
        {
          method: session ? 'PATCH' : 'POST',
          body: {
            courseId,
            startsOn,
            endsOn,
            location: location || null,
            trainer: trainer || null,
            capacity: capacity === '' ? null : Number(capacity),
            cost: cost === '' ? null : Number(cost),
            notes: notes || null,
          },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['learning'] });
      toast('حُفظ الانعقاد.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={session ? 'تعديل انعقاد' : 'انعقادُ دورة'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending || !courseId}
            onClick={() => save.mutate()}
          >
            احفظ
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="ts-course">
            الدورة
          </label>
          <select
            id="ts-course"
            className="select"
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            disabled={session !== undefined}
          >
            <option value="">— اختر —</option>
            {(courses.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
          {courses.data && courses.data.length === 0 && (
            <span className="tiny mute">
              لا دوراتٍ مفعّلةً في الكتالوج — اكتب دورةً أوّلًا.
            </span>
          )}
        </div>

        <div className="field">
          <label className="label req" htmlFor="ts-start">
            من
          </label>
          <input
            id="ts-start"
            className="input"
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="ts-end">
            إلى
          </label>
          <input
            id="ts-end"
            className="input"
            type="date"
            value={endsOn}
            onChange={(e) => setEndsOn(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ts-trainer">
            المدرّب
          </label>
          <input
            id="ts-trainer"
            className="input"
            value={trainer}
            onChange={(e) => setTrainer(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ts-location">
            المكان
          </label>
          <input
            id="ts-location"
            className="input"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ts-capacity">
            المقاعد
          </label>
          <input
            id="ts-capacity"
            className="input num"
            type="number"
            min={1}
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            placeholder="بلا حدّ"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ts-cost">
            تكلفة الانعقاد كلِّه
          </label>
          <input
            id="ts-cost"
            className="input num"
            type="number"
            min={0}
            value={cost}
            onChange={(e) => setCost(e.target.value)}
          />
          {/*
            التكلفةُ للانعقاد لا للمشارك: منها تُقسَم كلفةُ المقعد على من
            شغل مقعدًا — والمتغيّبُ دُفع ثمنُ مقعده.
          */}
          <span className="tiny mute">
            تُقسَم على من شغل مقعدًا — بمن فيهم من تغيّب.
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ts-notes">
            ملاحظات
          </label>
          <textarea
            id="ts-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   الأثر
   ========================================================================== */

function ImpactSection() {
  const impact = useQuery({
    queryKey: ['learning', 'impact'],
    queryFn: () => api<ImpactView>('/hr/learning/impact'),
  });

  const d = impact.data;

  return (
    <div className="card mt-3">
      <h2 className="card-title">الأثر والتكلفة</h2>

      {/*
        الجملةُ تسبق الرقم لا تتبعه: فارقُ محصّلةٍ قبل الدورة وبعدها ارتباطٌ
        لا سببيّة، ورقمٌ بلا هذه الجملة يُقرأ إثباتًا لما لا يُثبَت.
      */}
      <Notice title="ما يُقاس وما لا يُقاس" icon="info">
        يُقاس من حضر ممّن شغل مقعدًا، وكلفةُ المقعد، ورأيُ من حضر. أمّا «فارق
        الأداء» فهو فرقُ محصّلة تقييمٍ قبل الدورة وآخرَ بعدها — ارتباطٌ لا
        سببيّة: من حضر دورةً حضر معها سنةً فيها عشرةُ أسبابٍ أخرى. ولا يُعرض
        إلّا بثلاثة حاضرين لكلٍّ منهم تقييمٌ قبلها وبعدها.
      </Notice>

      {impact.isError && (
        <ErrorState
          error={impact.error}
          onRetry={() => void impact.refetch()}
        />
      )}
      {impact.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {d && d.rows.length === 0 && (
        <EmptyState
          title="لا انعقاداتٍ انتهت بعد"
          text="الأثرُ يُقرأ من انعقادٍ جرى أو انتهى — لا من انعقادٍ مجدول."
        />
      )}

      {d && d.rows.length > 0 && (
        <div className="table-wrap sticky-head mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الدورة</th>
                <th>انعقادات</th>
                <th>مقاعد</th>
                <th>حضر</th>
                <th>نسبة الحضور</th>
                <th>التكلفة</th>
                <th>كلفة المقعد</th>
                <th>رأي من حضر</th>
                <th>فارق الأداء</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.courseId}>
                  <td>
                    {r.title}
                    <div className="tiny mute">{r.trackLabel}</div>
                  </td>
                  <td className="num">{num(r.sessions)}</td>
                  <td className="num">{num(r.seats)}</td>
                  <td className="num">
                    {num(r.attended)}
                    {r.absent > 0 && (
                      <div className="tiny" style={{ color: 'var(--warning)' }}>
                        {num(r.absent)} تغيّب
                      </div>
                    )}
                  </td>
                  <td className="num">
                    {r.attendance === null ? '—' : `${num(r.attendance)}٪`}
                  </td>
                  <td className="num">
                    {r.cost === null ? '—' : money(r.cost)}
                  </td>
                  <td className="num">
                    {r.costPerSeat === null ? '—' : money(r.costPerSeat)}
                  </td>
                  <td className="num">
                    {r.rating === null ? (
                      <span className="tiny mute">{r.ratingReason}</span>
                    ) : (
                      `${num(r.rating)} / 5`
                    )}
                  </td>
                  <td className="num">
                    {r.delta === null ? (
                      <span className="tiny mute">{r.deltaReason}</span>
                    ) : (
                      `${r.delta > 0 ? '+' : ''}${num(r.delta)} نقطة مئويّة`
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
